import React from "react";
import { CalculatorState } from "@/types/calculatorTypes";
import { ContributionCalculator } from "./CalculationEngine";
import ButtonSlider from "../Slider/ButtonSlider";

interface DetailedCalculatorProps {
  calculatorState: CalculatorState;
  onSalaryChange: (salary: number) => void;
  onContributionRateChange: (rate: number) => void;
  onEmployerMatchRateChange: (rate: number) => void;
  onMaxEmployerMatchSalaryRateChange: (rate: number) => void;
  onAnnualGrowthRateChange: (rate: number) => void;
  onMaxAnnualContributionChange: (amount: number) => void;
}

const DetailedCalculator: React.FC<DetailedCalculatorProps> = ({
  calculatorState,
  onSalaryChange,
  onContributionRateChange,
  //onEmployerMatchRateChange,
  onMaxEmployerMatchSalaryRateChange,
  onAnnualGrowthRateChange,
}) => {
  const results = ContributionCalculator.calculate(calculatorState);

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleSalaryChange = (value: string) => {
    const numValue = parseInt(value.replace(/[^\d]/g, "")) || 0;
    onSalaryChange(numValue);
  };

  // Round values for ButtonSlider to prevent decimal display issues
  const roundedEmployeeContributionRate = Math.round(
    calculatorState.employeeContributionRate
  );
  const roundedMaxEmployerMatchSalaryRate = Math.round(
    calculatorState.maxEmployerMatchSalaryRate
  );
  const roundedAnnualGrowthRate = Math.round(calculatorState.annualGrowthRate);

  return (
    <>
      {/** Employee part */}

      <div className="flex flex-col space-y-[16px] pb-[32px] leading-[127%]">
        <div className="text-[18px] font-semibold">Your part</div>
        <div className="flex justify-between items-center">
          <p>Annual Salary</p>
          <div className="w-[65%]">
            <input
              type="text"
              value={formatCurrency(calculatorState.annualSalary)}
              onChange={(e) => handleSalaryChange(e.target.value)}
              style={{ boxShadow: "0px 1px 3px 0px rgba(0, 0, 0, 0.07) inset" }}
              className="w-full bg-[#F8F8F8] rounded-[100px] px-[12px] py-[16px] text-center text-[18px] font-normal text-black border-[1px] border-[#E2E2E2] outline-none focus:ring-2 focus:ring-black"
            />
          </div>
        </div>

        <ButtonSlider
          label="Contribution"
          value={roundedEmployeeContributionRate}
          onChange={onContributionRateChange}
          min={1}
          max={100}
        />
      </div>

      {/** Employers part */}
      <div className="flex flex-col space-y-[16px] border-y-[1px] border-[#E2E2E2] py-[32px] leading-[127%]">
        <div className="text-[18px] font-semibold">Employer&apos;s part</div>
        {/* <ButtonSlider
          label="Maximum employer match"
          value={calculatorState.employerMatchRate}
          onChange={onEmployerMatchRateChange}
          min={1}
          max={100}
        /> */}

        <ButtonSlider
          label="Annual max. employer match"
          value={roundedMaxEmployerMatchSalaryRate}
          onChange={onMaxEmployerMatchSalaryRateChange}
          min={1}
          max={100}
        />
      </div>

      {/** Investment */}
      <div className="flex flex-col space-y-[16px] border-y-[1px] border-[#E2E2E2] py-[32px] leading-[127%]">
        <div className="text-[18px] font-semibold">Investment</div>
        <ButtonSlider
          label="Yearly growth"
          value={roundedAnnualGrowthRate}
          onChange={onAnnualGrowthRateChange}
          min={1}
          max={20}
        />
      </div>

      {/** Resukt */}
      <div className="flex flex-col space-y-[16px] border-t-[1px] border-[#E2E2E2] py-[32px] leading-[127%]">
        <div className="text-[18px] font-semibold">Result</div>
        <div className="space-y-[16px] text-[16px] font-normal">
          <div className="flex justify-between">
            <span>Deduction per payroll</span>
            <span>
              {formatCurrency(results.employeeContributionPerPayroll)}
            </span>
          </div>

          <div className="flex justify-between">
            <span>Employer match per payroll</span>
            <span>{formatCurrency(results.employerMatchPerPayroll)}</span>
          </div>

          <div className="flex justify-between">
            <span>Total contribution per payroll</span>
            <span>{formatCurrency(results.totalContributionPerPayroll)}</span>
          </div>

          <div className="flex justify-between">
            <span>Saved after 12 months</span>
            <span>
              {formatCurrency(
                results.totalEmployeeContributions +
                  results.totalEmployerContributions
              )}
            </span>
            {/*  <span>{formatCurrency(results.totalWealthAfter12Months)}</span> */}
          </div>

          <div className="flex justify-between">
            <span>Investment gain</span>
            <span>{formatCurrency(results.investmentGrowth)}</span>
          </div>

          <div className="flex justify-between">
            <span>Total wealth after 12 months</span>
            <span>{formatCurrency(results.totalWealthAfter12Months)}</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default DetailedCalculator;
